import React, { createContext, useContext, useState, useEffect } from "react";
import toast from "react-hot-toast";
import apiClient from "../services/api/client";
import ENDPOINTS from "../services/api/endpoints";

const AuthContext = createContext();

/**
 * Default enabled modules (all modules enabled by default)
 * In production, this will come from API based on subscription
 */
const DEFAULT_ENABLED_MODULES = [
  'pos', 'kds', 'backoffice', 'inventory',
  'finance', 'crm', 'hrm', 'ai-insights',
  'integrations', 'captain-app', 'qr-menu'
];

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);


  useEffect(() => {
    const storedUser = sessionStorage.getItem("ecomm_notify_user");
    const storedToken = localStorage.getItem("token");

    if (storedUser && storedToken) {
      try {
        const parsedUser = JSON.parse(storedUser);
        // Always use the latest DEFAULT_ENABLED_MODULES to pick up new modules
        parsedUser.enabledModules = DEFAULT_ENABLED_MODULES;
        setUser(parsedUser);
        // Update sessionStorage with the latest modules
        sessionStorage.setItem("ecomm_notify_user", JSON.stringify(parsedUser));
      } catch (error) {
        console.error("Error parsing stored user:", error);
        sessionStorage.removeItem("ecomm_notify_user");
        localStorage.removeItem("token");
      }
    }
    setLoading(false);
  }, []);


  const login = async (username, password) => {
    try {
      if (!username || !password) {
        toast.error("Please enter username and password");
        return false;
      }
      const response = await apiClient.post(ENDPOINTS.AUTH.LOGIN, { username, password });

      // Note: apiClient response interceptor already unwraps response.data
      const { username: backendUsername, token, tokenType, expiryDate } = response;

      if (!token) {
        toast.error("Login failed: No token received");
        return false;
      }



      // Decode JWT payload to extract user details (handle base64url encoding)
      let firstName = '';
      let lastName = '';
      let branchId = null;
      let userType = null;
      let userId = null;
      try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(atob(base64));
        firstName = payload.firstname || '';
        lastName = payload.lastname || '';
        branchId = payload.branchIds || null;
        userType = payload.userType || null;
        userId = payload.userId || null;
      } catch (e) {
        console.warn('Failed to decode JWT payload', e);
      }

      const fullName = [firstName, lastName].filter(Boolean).join(' ') || backendUsername;

      const role =
        backendUsername.toLowerCase().includes("admin")
          ? "admin"
          : backendUsername.toLowerCase().includes("pos")
            ? "pos"
            : "both";


      const userData = {
        username: backendUsername,
        id: userId,
        name: fullName,
        firstName,
        lastName,
        branchId,
        userType,
        tokenType: tokenType?.trim() || "Bearer",
        expiryDate,
        role,
        // Module access - will come from API in future based on subscription
        // For now, all modules are enabled by default
        enabledModules: DEFAULT_ENABLED_MODULES,
        subscription: {
          plan: 'enterprise', // 'basic', 'pro', 'enterprise'
          expiresAt: null,    // Subscription expiry date
        },
      };


      localStorage.setItem("token", token);
      sessionStorage.setItem("ecomm_notify_user", JSON.stringify(userData));

      setUser(userData);
      toast.success(`Welcome back, ${fullName}!`);
      return true;
    } catch (error) {
      console.error("Login error:", error);
      if (error.response?.status === 401) {
        toast.error("Invalid username or password");
      } else {
        toast.error("Login failed. Please try again.");
      }
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    sessionStorage.removeItem("ecomm_notify_user");
    localStorage.removeItem("token");
    toast.success("Logged out successfully");
  };

  const isAuthenticated = () => !!user;
  const hasRole = (role) => user && (user.role === role || user.role === "both");

  /**
   * Check if user has access to a specific module
   * @param {string} moduleId - The module ID to check (e.g., 'pos', 'inventory', 'crm')
   * @returns {boolean} - True if user has access, false otherwise
   *
   * Future: This will check against subscription-based module access from API
   * Current: Returns true for all modules in DEFAULT_ENABLED_MODULES
   */
  const ADMIN_USER_TYPES = ['MANAGEMENT', 'ADMIN', 'SUPER_USER', 'TECHNICAL_USER', 'POWER_USER'];

  const isAdminOrManagement = () => {
    return ADMIN_USER_TYPES.includes(user?.userType);
  };

  const hasModuleAccess = (moduleId) => {
    // If no user or no enabledModules, allow all (development fallback)
    if (!user?.enabledModules) return true;
    return user.enabledModules.includes(moduleId);
  };

  /**
   * Get user's subscription info
   * @returns {object} - Subscription details { plan, expiresAt }
   */
  const getSubscription = () => {
    return user?.subscription || { plan: 'basic', expiresAt: null };
  };

  /**
   * Get list of enabled modules for current user
   * @returns {string[]} - Array of enabled module IDs
   */
  const getEnabledModules = () => {
    return user?.enabledModules || DEFAULT_ENABLED_MODULES;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        isAuthenticated,
        hasRole,
        isAdminOrManagement,
        hasModuleAccess,
        getSubscription,
        getEnabledModules,
        loading
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};